import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Alert,
  Paper,
  List,
  ListItem,
  ListItemText,
  Chip,
  Divider,
  Snackbar,
  Button,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress,
  Badge
} from '@mui/material';
import {
  TrendingUp,
  EventNote,
  Login as CheckIn,
  Logout as CheckOut,
  People,
  Today as TodayIcon,
  Sync as SyncIcon,
  Error as ErrorIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  Lock as LockIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import reservationService from '../services/reservationService';
import { useLanguage } from '../App';

// Dashboard translations
const dashboardTranslations = {
  en: {
    dashboard_title: 'Dashboard',
    total_reservations: 'Total Reservations',
    active_reservations: 'Active Reservations',
    checking_in_today: 'Checking In Today',
    checking_out_today: 'Checking Out Today',
    recent_reservations: 'Recent Reservations',
    reservation_by_channel: 'Reservations by Channel',
    reservation_by_status: 'Reservations by Status',
    daily_actions: 'Daily Actions',
    reservations_24h: 'Reservations (Next 24h)',
    ready_for_integration: 'Ready for Integration',
    guest_info_pending: 'Guest Info Pending',
    guest_info_provided: 'Guest Info Provided',
    error_status: 'Error',
    sync_password: 'Sync Password',
    clear_error: 'Clear Error',
    view_details: 'View Details',
    loading_stats: 'Loading statistics...',
    error_loading_stats: 'Error loading dashboard statistics',
    no_recent_reservations: 'No recent reservations',
    no_daily_actions: 'No reservations requiring action in the next 24 hours',
    guests: 'guests',
    nights: 'nights',
    password_sync_success: 'Password synced successfully',
    password_sync_error: 'Failed to sync password',
    error_cleared: 'Error cleared successfully',
    confirm_sync: 'Confirm Password Sync',
    confirm_sync_message: 'Generate and sync password for this reservation?',
    confirm_clear_error: 'Clear Error',
    confirm_clear_error_message: 'Clear the error status for this reservation?',
    processing: 'Processing...',
    property: 'Property',
    lock_id: 'Lock ID',
    check_in_time: 'Check-in',
    check_out_time: 'Check-out',
    password_generated: 'Password Generated',
    error_details: 'Error Details'
  },
  pt: {
    dashboard_title: 'Painel',
    total_reservations: 'Total de Reservas',
    active_reservations: 'Reservas Ativas',
    checking_in_today: 'Check-in Hoje',
    checking_out_today: 'Check-out Hoje',
    recent_reservations: 'Reservas Recentes',
    reservation_by_channel: 'Reservas por Canal',
    reservation_by_status: 'Reservas por Status',
    daily_actions: 'Ações Diárias',
    reservations_24h: 'Reservas (Próximas 24h)',
    ready_for_integration: 'Pronto para Integração',
    guest_info_pending: 'Info do Hóspede Pendente',
    guest_info_provided: 'Info do Hóspede Fornecida',
    error_status: 'Erro',
    sync_password: 'Sincronizar Senha',
    clear_error: 'Limpar Erro',
    view_details: 'Ver Detalhes',
    loading_stats: 'Carregando estatísticas...',
    error_loading_stats: 'Erro ao carregar estatísticas do painel',
    no_recent_reservations: 'Nenhuma reserva recente',
    no_daily_actions: 'Nenhuma reserva requer ação nas próximas 24 horas',
    guests: 'hóspedes',
    nights: 'noites',
    password_sync_success: 'Senha sincronizada com sucesso',
    password_sync_error: 'Falha ao sincronizar senha',
    error_cleared: 'Erro limpo com sucesso',
    confirm_sync: 'Confirmar Sincronização de Senha',
    confirm_sync_message: 'Gerar e sincronizar senha para esta reserva?',
    confirm_clear_error: 'Limpar Erro',
    confirm_clear_error_message: 'Limpar o status de erro para esta reserva?',
    processing: 'Processando...',
    property: 'Propriedade',
    lock_id: 'ID da Fechadura',
    check_in_time: 'Check-in',
    check_out_time: 'Check-out',
    password_generated: 'Senha Gerada',
    error_details: 'Detalhes do Erro'
  },
  es: {
    dashboard_title: 'Panel',
    total_reservations: 'Total de Reservas',
    active_reservations: 'Reservas Activas',
    checking_in_today: 'Check-in Hoy',
    checking_out_today: 'Check-out Hoy',
    recent_reservations: 'Reservas Recientes',
    reservation_by_channel: 'Reservas por Canal',
    reservation_by_status: 'Reservas por Estado',
    daily_actions: 'Acciones Diarias',
    reservations_24h: 'Reservas (Próximas 24h)',
    ready_for_integration: 'Listo para Integración',
    guest_info_pending: 'Info del Huésped Pendiente',
    guest_info_provided: 'Info del Huésped Proporcionada',
    error_status: 'Error',
    sync_password: 'Sincronizar Contraseña',
    clear_error: 'Limpiar Error',
    view_details: 'Ver Detalles',
    loading_stats: 'Cargando estadísticas...',
    error_loading_stats: 'Error al cargar estadísticas del panel',
    no_recent_reservations: 'No hay reservas recientes',
    no_daily_actions: 'No hay reservas que requieran acción en las próximas 24 horas',
    guests: 'huéspedes',
    nights: 'noches',
    password_sync_success: 'Contraseña sincronizada exitosamente',
    password_sync_error: 'Error al sincronizar contraseña',
    error_cleared: 'Error limpiado exitosamente',
    confirm_sync: 'Confirmar Sincronización de Contraseña',
    confirm_sync_message: '¿Generar y sincronizar contraseña para esta reserva?',
    confirm_clear_error: 'Limpiar Error',
    confirm_clear_error_message: '¿Limpiar el estado de error para esta reserva?',
    processing: 'Procesando...',
    property: 'Propiedad',
    lock_id: 'ID de Cerradura',
    check_in_time: 'Check-in',
    check_out_time: 'Check-out',
    password_generated: 'Contraseña Generada',
    error_details: 'Detalles del Error'
  }
};

function StatCard({ title, value, icon, color = 'primary' }) {
  return (
    <Card>
      <CardContent>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box>
            <Typography color="textSecondary" gutterBottom variant="body2">
              {title}
            </Typography>
            <Typography variant="h4" component="h2">
              {value}
            </Typography>
          </Box>
          <Box color={`${color}.main`}>
            {icon}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
}

function Dashboard() {
  const [stats, setStats] = useState(null);
  const [dailyActions, setDailyActions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const [confirmDialog, setConfirmDialog] = useState({ open: false, type: '', reservation: null });
  const [processing, setProcessing] = useState(false);
  const { language } = useLanguage();

  const t = (key) => {
    return dashboardTranslations[language]?.[key] || dashboardTranslations.en[key] || key;
  };

  useEffect(() => {
    loadDashboardData();
    // Auto-refresh every 5 minutes
    const interval = setInterval(loadDashboardData, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      setError('');
      
      // Load dashboard stats
      const dashboardStats = await reservationService.getReservationStats();
      setStats(dashboardStats);
      
      // Load daily actions (reservations in next 24 hours)
      const dailyActionsData = await loadDailyActions();
      setDailyActions(dailyActionsData);
      
    } catch (err) {
      setSnackbar({ open: true, message: `${t('error_loading_stats')}: ${err.message}`, severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const loadDailyActions = async () => {
    try {
      // Get all reservations
      const response = await reservationService.getReservations({ limit: 1000 });
      const reservations = response.reservations || [];
      
      // Filter reservations for today and next 24 hours
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const next24Hours = new Date(now.getTime() + 24 * 60 * 60 * 1000);
      
      const dailyReservations = reservations.filter(reservation => {
        // Handle date-only strings (YYYY-MM-DD) to avoid timezone issues
        let checkInDate;
        if (reservation.checkInDate.match(/^\d{4}-\d{2}-\d{2}$/)) {
          const [year, month, day] = reservation.checkInDate.split('-');
          checkInDate = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
        } else {
          checkInDate = new Date(reservation.checkInDate);
        }
        
        // Include reservations checking in today or within next 24 hours
        return checkInDate >= today && checkInDate <= next24Hours;
      });
      
      // Process status updates automatically
      const updatedReservations = await Promise.all(
        dailyReservations.map(async (reservation) => {
          return await processReservationStatus(reservation);
        })
      );
      
      return updatedReservations;
    } catch (error) {
      console.error('Error loading daily actions:', error);
      return [];
    }
  };

  const processReservationStatus = async (reservation) => {
    try {
      let updatedReservation = { ...reservation };
      let needsUpdate = false;
      
      // Auto-update status based on conditions
      if (reservation.status === 'Guest Information Provided') {
        // Check if within 24 hours of check-in
        let checkInDate;
        if (reservation.checkInDate.match(/^\d{4}-\d{2}-\d{2}$/)) {
          const [year, month, day] = reservation.checkInDate.split('-');
          checkInDate = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
        } else {
          checkInDate = new Date(reservation.checkInDate);
        }
        
        const now = new Date();
        const hoursUntilCheckIn = (checkInDate - now) / (1000 * 60 * 60);
        
        if (hoursUntilCheckIn <= 24) {
          updatedReservation.status = 'Ready for Integration';
          needsUpdate = true;
        }
      }
      
      // Clear errors if all guests have passwords
      if (reservation.status === 'Error' && reservation.errorDetails) {
        const allGuestsHavePasswords = checkAllGuestsHavePasswords(reservation);
        if (allGuestsHavePasswords) {
          updatedReservation.status = 'Ready for Integration';
          updatedReservation.errorDetails = null;
          needsUpdate = true;
        }
      }
      
      // Update reservation if needed
      if (needsUpdate) {
        await reservationService.updateReservation(reservation.confirmationCode, {
          status: updatedReservation.status,
          errorDetails: updatedReservation.errorDetails,
          updatedAt: new Date().toISOString()
        });
      }
      
      return updatedReservation;
    } catch (error) {
      console.error('Error processing reservation status:', error);
      return reservation;
    }
  };

  const checkAllGuestsHavePasswords = (reservation) => {
    if (!reservation.guestInformation || !Array.isArray(reservation.guestInformation)) {
      return false;
    }
    
    return reservation.guestInformation.every(guest => 
      guest.password && guest.password.trim() !== ''
    );
  };

  const handleSyncPassword = async (reservation) => {
    setConfirmDialog({
      open: true,
      type: 'sync',
      reservation: reservation
    });
  };

  const handleClearError = async (reservation) => {
    setConfirmDialog({
      open: true,
      type: 'clear_error',
      reservation: reservation
    });
  };

  const confirmAction = async () => {
    const { type, reservation } = confirmDialog;
    setProcessing(true);
    
    try {
      if (type === 'sync') {
        await syncReservationPasswords(reservation);
      } else if (type === 'clear_error') {
        await clearReservationError(reservation);
      }
      
      // Reload daily actions
      const updatedDailyActions = await loadDailyActions();
      setDailyActions(updatedDailyActions);
      
    } catch (error) {
      setSnackbar({ 
        open: true, 
        message: `Error: ${error.message}`, 
        severity: 'error' 
      });
    } finally {
      setProcessing(false);
      setConfirmDialog({ open: false, type: '', reservation: null });
    }
  };

  const syncReservationPasswords = async (reservation) => {
    try {
      // Get property information for lock details
      const properties = JSON.parse(localStorage.getItem('properties') || '[]');
      const property = properties.find(p => p.name === reservation.listingNickname);
      
      if (!property || !property.lockId) {
        throw new Error('Property lock configuration not found');
      }
      
      // Calculate access times with buffers
      const accessTimes = calculateAccessTimes(reservation, property);
      
      // Generate passwords for each guest
      const updatedGuests = await Promise.all(
        (reservation.guestInformation || []).map(async (guest, index) => {
          try {
            // Use last 6 digits of phone number, fallback to random
            let password = extractLast6Digits(guest.phone);
            
            // Call TTLock Lambda function
            const ttlockResponse = await fetch('/api/ttlock/passcode', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${localStorage.getItem('jwt_token')}`
              },
              body: JSON.stringify({
                name: `${guest.firstName} ${guest.lastName}`,
                start_date: accessTimes.startDate,
                end_date: accessTimes.endDate,
                lock_id: property.lockId,
                password: password
              })
            });
            
            if (!ttlockResponse.ok) {
              throw new Error(`TTLock API error: ${ttlockResponse.statusText}`);
            }
            
            const result = await ttlockResponse.json();
            
            return {
              ...guest,
              password: result.password,
              passwordSyncedAt: new Date().toISOString()
            };
            
          } catch (error) {
            console.error(`Error syncing password for guest ${index + 1}:`, error);
            throw error;
          }
        })
      );
      
      // Update reservation with passwords
      await reservationService.updateReservation(reservation.confirmationCode, {
        guestInformation: updatedGuests,
        status: 'Ready for Integration',
        passwordsSyncedAt: new Date().toISOString(),
        errorDetails: null
      });
      
      setSnackbar({ 
        open: true, 
        message: t('password_sync_success'), 
        severity: 'success' 
      });
      
    } catch (error) {
      // Update reservation with error
      await reservationService.updateReservation(reservation.confirmationCode, {
        status: 'Error',
        errorDetails: `Password sync failed: ${error.message}`,
        lastErrorAt: new Date().toISOString()
      });
      
      throw error;
    }
  };

  const clearReservationError = async (reservation) => {
    await reservationService.updateReservation(reservation.confirmationCode, {
      status: 'Ready for Integration',
      errorDetails: null,
      lastErrorAt: null
    });
    
    setSnackbar({ 
      open: true, 
      message: t('error_cleared'), 
      severity: 'success' 
    });
  };

  const calculateAccessTimes = (reservation, property) => {
    const checkInDate = new Date(`${reservation.checkInDate}T${property.checkInTime || '15:00'}:00`);
    const checkOutDate = new Date(`${reservation.checkOutDate}T${property.checkOutTime || '11:00'}:00`);
    
    // Apply buffers
    const checkInBuffer = parseInt(property.checkInBuffer || 0);
    const checkOutBuffer = parseInt(property.checkOutBuffer || 0);
    
    const startTime = new Date(checkInDate.getTime() - (checkInBuffer * 60 * 1000));
    const endTime = new Date(checkOutDate.getTime() + (checkOutBuffer * 60 * 1000));
    
    return {
      startDate: formatDateForTTLock(startTime),
      endDate: formatDateForTTLock(endTime)
    };
  };

  const formatDateForTTLock = (date) => {
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    
    return `${day}-${month}-${year} ${hours}:${minutes}`;
  };

  const extractLast6Digits = (phone) => {
    if (!phone) return null;
    
    // Extract only digits
    const digits = phone.replace(/\D/g, '');
    
    // Get last 6 digits, pad with zeros if needed
    if (digits.length >= 6) {
      return digits.slice(-6);
    } else if (digits.length >= 4) {
      return digits.padStart(6, '0');
    }
    
    return null; // Let TTLock generate random
  };

  const handleManualAutomation = async () => {
    setProcessing(true);
    
    try {
      // Call manual override Lambda function
      const response = await fetch('/api/daily-actions/manual', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('jwt_token')}`
        },
        body: JSON.stringify({
          manual_override: true,
          auth_token: localStorage.getItem('jwt_token'),
          user: 'dashboard',
          timestamp: new Date().toISOString()
        })
      });
      
      if (!response.ok) {
        throw new Error(`Manual automation failed: ${response.statusText}`);
      }
      
      const result = await response.json();
      
      if (result.successful > 0) {
        setSnackbar({ 
          open: true, 
          message: `Manual automation completed: ${result.successful} reservations processed`, 
          severity: 'success' 
        });
      } else {
        setSnackbar({ 
          open: true, 
          message: 'No reservations required automation at this time', 
          severity: 'info' 
        });
      }
      
      // Reload daily actions
      const updatedDailyActions = await loadDailyActions();
      setDailyActions(updatedDailyActions);
      
    } catch (error) {
      setSnackbar({ 
        open: true, 
        message: `Manual automation error: ${error.message}`, 
        severity: 'error' 
      });
    } finally {
      setProcessing(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'booking confirmed':
        return 'success';
      case 'guest information provided':
        return 'info';
      case 'ready for integration':
        return 'warning';
      case 'checked in':
        return 'info';
      case 'checked out':
        return 'default';
      case 'cancelled':
        return 'error';
      case 'deleted':
        return 'error';
      case 'error':
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusIcon = (status) => {
    switch (status?.toLowerCase()) {
      case 'ready for integration':
        return <ScheduleIcon fontSize="small" />;
      case 'error':
        return <ErrorIcon fontSize="small" />;
      case 'guest information provided':
        return <CheckCircleIcon fontSize="small" />;
      default:
        return <EventNote fontSize="small" />;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      // Handle date-only strings (YYYY-MM-DD) to avoid timezone issues
      if (dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
        const [year, month, day] = dateString.split('-');
        const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
        return date.toLocaleDateString();
      }
      return new Date(dateString).toLocaleDateString();
    } catch {
      return dateString;
    }
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      // Handle date-only strings (YYYY-MM-DD) to avoid timezone issues
      if (dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
        const [year, month, day] = dateString.split('-');
        const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
        return date.toLocaleDateString();
      }
      return new Date(dateString).toLocaleString();
    } catch {
      return dateString;
    }
  };

  const getHoursUntilCheckIn = (checkInDate) => {
    const now = new Date();
    
    // Handle date-only strings (YYYY-MM-DD) to avoid timezone issues
    let checkIn;
    if (checkInDate.match(/^\d{4}-\d{2}-\d{2}$/)) {
      const [year, month, day] = checkInDate.split('-');
      checkIn = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
    } else {
      checkIn = new Date(checkInDate);
    }
    
    const hours = Math.round((checkIn - now) / (1000 * 60 * 60));
    return Math.max(0, hours);
  };

  const renderDailyActionItem = (reservation) => {
    const hoursUntil = getHoursUntilCheckIn(reservation.checkInDate);
    const hasPasswords = checkAllGuestsHavePasswords(reservation);
    
    return (
      <ListItem key={reservation.confirmationCode} sx={{ border: 1, borderColor: 'divider', borderRadius: 1, mb: 1 }}>
        <ListItemText
          primary={
            <Box display="flex" alignItems="center" gap={1} justifyContent="space-between">
              <Box display="flex" alignItems="center" gap={1}>
                {getStatusIcon(reservation.status)}
                <Typography variant="subtitle1" fontWeight="bold">
                  {reservation.guestFirstName} {reservation.guestLastName}
                </Typography>
                <Chip
                  label={reservation.status || 'Unknown'}
                  size="small"
                  color={getStatusColor(reservation.status)}
                />
                {hoursUntil <= 6 && (
                  <Chip
                    label={`${hoursUntil}h`}
                    size="small"
                    color="error"
                    variant="outlined"
                  />
                )}
              </Box>
              <Box display="flex" gap={1}>
                {reservation.status === 'Ready for Integration' && !hasPasswords && (
                  <Tooltip title={t('sync_password')}>
                    <IconButton
                      size="small"
                      color="primary"
                      onClick={() => handleSyncPassword(reservation)}
                      disabled={processing}
                    >
                      <SyncIcon />
                    </IconButton>
                  </Tooltip>
                )}
                {reservation.status === 'Error' && (
                  <Tooltip title={t('clear_error')}>
                    <IconButton
                      size="small"
                      color="warning"
                      onClick={() => handleClearError(reservation)}
                      disabled={processing}
                    >
                      <RefreshIcon />
                    </IconButton>
                  </Tooltip>
                )}
                {hasPasswords && (
                  <Tooltip title={t('password_generated')}>
                    <IconButton size="small" color="success">
                      <LockIcon />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
            </Box>
          }
          secondary={
            <Box>
              <Typography variant="body2" color="textSecondary">
                {t('property')}: {reservation.listingNickname} | 
                Confirmação: {reservation.confirmationCode}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {t('check_in_time')}: {formatDateTime(reservation.checkInDate)} | 
                {reservation.numberOfGuests} {t('guests')}
              </Typography>
              {reservation.errorDetails && (
                <Typography variant="body2" color="error" sx={{ mt: 0.5 }}>
                  {t('error_details')}: {reservation.errorDetails}
                </Typography>
              )}
              {hasPasswords && (
                <Typography variant="body2" color="success.main" sx={{ mt: 0.5 }}>
                  ✓ {t('password_generated')} ({reservation.guestInformation?.length || 0} guests)
                </Typography>
              )}
            </Box>
          }
        />
      </ListItem>
    );
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>{t('loading_stats')}</Typography>
      </Box>
    );
  }

  if (error) {
    // Show error via snackbar instead of blocking the entire page
    return (
      <Box>
        <Typography variant="h4" gutterBottom>
          {t('dashboard_title')}
        </Typography>
        <Typography color="textSecondary">
          Dados não disponíveis no momento.
        </Typography>
        <Snackbar
          open={true}
          autoHideDuration={6000}
        >
          <Alert severity="error">
            {t('error_loading_stats')}: {error}
          </Alert>
        </Snackbar>
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" gutterBottom>
          {t('dashboard_title')} 📊
        </Typography>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={loadDashboardData}
          disabled={loading}
        >
          Refresh
        </Button>
      </Box>
      
      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title={t('total_reservations')}
            value={stats?.total || 0}
            icon={<EventNote fontSize="large" />}
            color="primary"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title={t('active_reservations')}
            value={stats?.active || 0}
            icon={<TrendingUp fontSize="large" />}
            color="success"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title={t('checking_in_today')}
            value={stats?.checkingInToday || 0}
            icon={<CheckIn fontSize="large" />}
            color="info"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Badge badgeContent={dailyActions.length} color="warning">
            <StatCard
              title={t('reservations_24h')}
              value={dailyActions.length}
              icon={<TodayIcon fontSize="large" />}
              color="warning"
            />
          </Badge>
        </Grid>
      </Grid>

      {/* Daily Actions Section */}
      {dailyActions.length > 0 && (
        <Paper sx={{ p: 2, mb: 3 }}>
          <Box display="flex" alignItems="center" gap={1} mb={2} justifyContent="space-between">
            <Box display="flex" alignItems="center" gap={1}>
              <TodayIcon color="warning" />
              <Typography variant="h6">
                {t('daily_actions')}
              </Typography>
              <Chip 
                label={dailyActions.length} 
                size="small" 
                color="warning" 
              />
            </Box>
            <Button
              variant="outlined"
              size="small"
              startIcon={<SyncIcon />}
              onClick={handleManualAutomation}
              disabled={processing}
              color="primary"
            >
              Run Automation
            </Button>
          </Box>
          
          {processing && <LinearProgress sx={{ mb: 2 }} />}
          
          <List>
            {dailyActions.map(renderDailyActionItem)}
          </List>
        </Paper>
      )}

      <Grid container spacing={3}>
        {/* Recent Reservations */}
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              {t('recent_reservations')}
            </Typography>
            {stats?.recentReservations?.length > 0 ? (
              <List>
                {stats.recentReservations.map((reservation, index) => (
                  <React.Fragment key={reservation.confirmationCode}>
                    <ListItem>
                      <ListItemText
                        primary={
                          <Box display="flex" alignItems="center" gap={1}>
                            <Typography variant="subtitle1">
                              {reservation.guestFirstName} {reservation.guestLastName}
                            </Typography>
                            <Chip
                              label={reservation.status || 'Unknown'}
                              size="small"
                              color={getStatusColor(reservation.status)}
                            />
                          </Box>
                        }
                        secondary={
                          <Box>
                            <Typography variant="body2" color="textSecondary">
                              Confirmação: {reservation.confirmationCode}
                            </Typography>
                            <Typography variant="body2" color="textSecondary">
                              Check-in: {formatDate(reservation.checkInDate)} | 
                              Check-out: {formatDate(reservation.checkOutDate)}
                            </Typography>
                            {reservation.listingNickname && (
                              <Typography variant="body2" color="textSecondary">
                                Propriedade: {reservation.listingNickname}
                              </Typography>
                            )}
                            {reservation.numberOfGuests && (
                              <Typography variant="body2" color="textSecondary">
                                {reservation.numberOfGuests} {t('guests')}
                              </Typography>
                            )}
                          </Box>
                        }
                      />
                    </ListItem>
                    {index < stats.recentReservations.length - 1 && <Divider />}
                  </React.Fragment>
                ))}
              </List>
            ) : (
              <Typography color="textSecondary">
                {t('no_recent_reservations')}
              </Typography>
            )}
          </Paper>
        </Grid>

        {/* Stats Breakdown */}
        <Grid item xs={12} md={4}>
          <Grid container spacing={2}>
            {/* Channel Distribution */}
            <Grid item xs={12}>
              <Paper sx={{ p: 2 }}>
                <Typography variant="h6" gutterBottom>
                  {t('reservation_by_channel')}
                </Typography>
                {Object.entries(stats?.byChannel || {}).map(([channel, count]) => (
                  <Box key={channel} display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                    <Typography variant="body2">{channel}</Typography>
                    <Chip label={count} size="small" />
                  </Box>
                ))}
                {Object.keys(stats?.byChannel || {}).length === 0 && (
                  <Typography color="textSecondary" variant="body2">
                    Nenhum dado disponível
                  </Typography>
                )}
              </Paper>
            </Grid>

            {/* Status Distribution */}
            <Grid item xs={12}>
              <Paper sx={{ p: 2 }}>
                <Typography variant="h6" gutterBottom>
                  {t('reservation_by_status')}
                </Typography>
                {Object.entries(stats?.byStatus || {}).map(([status, count]) => (
                  <Box key={status} display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                    <Typography variant="body2">{status}</Typography>
                    <Chip 
                      label={count} 
                      size="small" 
                      color={getStatusColor(status)}
                    />
                  </Box>
                ))}
                {Object.keys(stats?.byStatus || {}).length === 0 && (
                  <Typography color="textSecondary" variant="body2">
                    No data available
                  </Typography>
                )}
              </Paper>
            </Grid>
          </Grid>
        </Grid>
      </Grid>

      {/* Confirmation Dialog */}
      <Dialog
        open={confirmDialog.open}
        onClose={() => !processing && setConfirmDialog({ open: false, type: '', reservation: null })}
      >
        <DialogTitle>
          {confirmDialog.type === 'sync' ? t('confirm_sync') : t('confirm_clear_error')}
        </DialogTitle>
        <DialogContent>
          <Typography>
            {confirmDialog.type === 'sync' ? t('confirm_sync_message') : t('confirm_clear_error_message')}
          </Typography>
          {confirmDialog.reservation && (
            <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
              <Typography variant="body2">
                <strong>Guest:</strong> {confirmDialog.reservation.guestFirstName} {confirmDialog.reservation.guestLastName}
              </Typography>
              <Typography variant="body2">
                <strong>Property:</strong> {confirmDialog.reservation.listingNickname}
              </Typography>
              <Typography variant="body2">
                <strong>Check-in:</strong> {formatDateTime(confirmDialog.reservation.checkInDate)}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setConfirmDialog({ open: false, type: '', reservation: null })}
            disabled={processing}
          >
            {t('cancel')}
          </Button>
          <Button 
            onClick={confirmAction} 
            variant="contained"
            disabled={processing}
            startIcon={processing ? <CircularProgress size={16} /> : null}
          >
            {processing ? t('processing') : (confirmDialog.type === 'sync' ? t('sync_password') : t('clear_error'))}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default Dashboard;
